'use client';

import React, { useState, useCallback, useMemo } from 'react';
import { Locale } from '@fixter/i18n';
import classNames from 'classnames';
import { createPostcodeSchema, sanitizeString, createVrmSchema, VrmLookupError } from '@/util/vrmLookupUtils';
import { Icon } from '@/components/atoms/Icon';
import { Button } from '@/components/atoms/Button';
import PostcodeAutocompleteV2 from '@/components/molecules/PostcodeAutocompleteV2';
import styles from './index.module.scss';

interface SubmitData {
  readonly vrm: string;
  readonly postcode: string;
}

interface Props {
  locale: Locale;
  translations: {
    textPostcode: string;
    textPostcodeInvalid: string;
    textPostcodeRequired: string;
    textRegistrationNumber: string;
    textRegistrationNumberInvalid: string;
    textRegistrationNumberRequired: string;
    textCtaGetInstantQuote: string;
  };
  className?: string;
}

/**
 *  @deprecated use VrmLookupForm instead
 */
export default function VrmLookupFormDesktop({ locale, translations, className }: Props): JSX.Element {
  const [formState, setFormState] = useState({ vrm: '', postcode: '' });
  const [errors, setErrors] = useState({ vrm: '', postcode: '' });
  const [focusedInput, setFocusedInput] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);

  const {
    textPostcode,
    textPostcodeInvalid,
    textPostcodeRequired,
    textRegistrationNumber,
    textRegistrationNumberInvalid,
    textRegistrationNumberRequired,
    textCtaGetInstantQuote,
  } = translations;

  const postcodeSchema = useMemo(
    () => createPostcodeSchema(locale, textPostcodeInvalid, textPostcodeRequired),
    [locale, textPostcodeInvalid, textPostcodeRequired]
  );

  const vrmSchema = useMemo(
    () => createVrmSchema(locale, textRegistrationNumberInvalid, textRegistrationNumberRequired),
    [locale, textRegistrationNumberInvalid, textRegistrationNumberRequired]
  );

  const handleFocus = (inputName: string) => {
    setFocusedInput(inputName);
  };

  const handleBlur = () => {
    setFocusedInput('');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const key = name as keyof typeof formState;
    setFormState((prevState) => ({ ...prevState, [key]: value }));
    if (errors[key]) {
      setErrors((prevErrors) => ({ ...prevErrors, [name]: '' }));
    }
  };

  const handlePostcodeSelect = (selectedPostcode: string) => {
    setFormState((prevState) => ({ ...prevState, postcode: selectedPostcode }));
    if (errors.postcode) {
      setErrors((prevErrors) => ({ ...prevErrors, postcode: '' }));
    }
  };

  const validateForm = useCallback(
    (formData: SubmitData) => {
      try {
        const { vrm, postcode } = formData;
        if (!vrm || !postcode) {
          setErrors({ vrm: textRegistrationNumberRequired, postcode: textPostcodeRequired });
          return false;
        }

        postcodeSchema.validate(sanitizeString(postcode));
        vrmSchema.validate(sanitizeString(vrm));

        setErrors({ vrm: '', postcode: '' });
        return true;
      } catch (err) {
        if (err instanceof VrmLookupError) {
          const postcode = err.type === 'PostcodeError' ? err.message : '';
          const vrm = err.type === 'VrmError' ? err.message : '';
          const formErrors = { postcode, vrm };

          setErrors(formErrors);
        }
        return false;
      }
    },
    [postcodeSchema, textPostcodeRequired, textRegistrationNumberRequired, vrmSchema]
  );

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      const { vrm, postcode } = formState;
      if (!vrm || !postcode) {
        setErrors({
          vrm: !vrm ? textRegistrationNumberRequired : '',
          postcode: !postcode ? textPostcodeRequired : '',
        });
        return;
      }
      const isValid = validateForm(formState);
      if (isValid) {
        setSubmitLoading(true);

        const submitData = {
          vrm: sanitizeString(vrm),
          postcode: sanitizeString(postcode),
        };

        const query = new URLSearchParams(submitData).toString();
        window.location.href = `/vehicles/add?${query}`;
      }
    },
    [formState, textPostcodeRequired, textRegistrationNumberRequired, validateForm]
  );

  return (
    <form className={`${styles.form} ${className || ''}`} onSubmit={handleSubmit}>
      <div className={styles.inputContainer}>
        <PostcodeAutocompleteV2
          translations={{ textPostcode }}
          errorMessage={errors.postcode}
          useAutocomplete={locale === Locale.EN_GB}
          inputClassName={styles.input}
          onChange={handleChange}
          onPostcodeSelect={handlePostcodeSelect}
        />
      </div>
      <div className={styles.inputDivider} />
      <div className={styles.inputContainer}>
        <Icon
          id="icon_car"
          className={classNames(styles.icon, { [styles.iconFocused]: focusedInput === 'vrm' })}
        />
        <input
          type="text"
          name="vrm"
          onChange={handleChange}
          className={classNames(styles.input, { [styles.inputError]: errors.vrm })}
          placeholder={textRegistrationNumber}
          onFocus={() => handleFocus('vrm')}
          onBlur={handleBlur}
        />
        {errors.vrm && (
          <div className={classNames(styles.errorMessage, { [styles.visible]: errors.vrm })}>
            {errors.vrm}
          </div>
        )}
      </div>
      <Button className={styles.button} type="submit" fullWidth loading={submitLoading}>
        {textCtaGetInstantQuote}
      </Button>
    </form>
  );
}
VrmLookupFormDesktop.displayName = 'VrmLookupFormDesktop';
